// frontend/src/pages/Company/CompanyList.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Edit, Plus, Trash2, Eye, Loader2, Building2, Search, Users, Shield, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import "./CompanyForm.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();
  const { currentUser, userType, company, loading: authLoading } = useAuth();

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/company`, {
        headers: getAuthHeaders(),
      });

      setCompanies(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const getFilteredCompanies = () => {
    let filtered = companies;
    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.company_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.business_type?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.country?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will also delete all related users, business types, currencies, and countries. This action cannot be undone.`)) {
      return;
    }
    try {
      setDeleting(id);
      await axios.delete(`${url}/api/company/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(`"${name}" deleted successfully`);
      fetchCompanies();
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to delete company";
      toast.error(errorMsg);
    } finally {
      setDeleting(null);
    }
  };

  const handleView = (id) => navigate(`/company/${id}`);
  const handleEdit = (id) => navigate(`/company/edit/${id}`);
  const handleAddCompany = () => navigate("/AddCompany");
  const handleAddUser = (companyId) => navigate(`/company/${companyId}/add-user`);

  if (authLoading || (loading && companies.length === 0)) {
    return (
      <div className="form-wrapper">
        <div className="company-list-container">
          <div className="loading-container">
            <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #f89a3a', borderTopColor: 'transparent', borderRadius: '50%' }}></div>
            <p>Loading companies...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredCompanies = getFilteredCompanies();
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  return (
    <div className="form-wrapper">
      <div className="company-list-container">
        <div className="header">
          <div className="header-left">
            <Building2 size={24} color="white" />
            <h2 className="header-title">
              {userType === 'company' ? 'My Company' : 'Companies Management'}
              {userType === 'company' && company && (
                <span className="company-badge" style={{
                  marginLeft: '10px', fontSize: '14px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  padding: '2px 8px', borderRadius: '4px'
                }}>
                  {company.company_name}
                </span>
              )}
            </h2>
            <span className="company-count">
              {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
              {userType === 'company' && ' (Your company)'}
            </span>
          </div>

          {isSystemAdmin && (
            <button className="submit-btn" onClick={handleAddCompany}>
              <Plus size={16} className="mr-2" /> Add Company
            </button>
          )}
        </div>

        {/* User info badge */}
        <div className="user-info-badge" style={{
          backgroundColor: 'rgba(248, 154, 58, 0.1)',
          border: '1px solid rgba(248, 154, 58, 0.3)',
          borderRadius: '8px', padding: '10px 15px',
          marginBottom: '20px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              backgroundColor: userType === 'system' ? '#3b82f6' : '#f59e0b',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white'
            }}>
              {userType === 'system' ? <Shield size={18} /> : <User size={18} />}
            </div>
            <div>
              <div style={{ fontWeight: 'bold' }}>
                {currentUser?.name || currentUser?.Usercomp_sure_name}
                <span style={{
                  marginLeft: '8px', fontSize: '12px',
                  backgroundColor: userType === 'system' ? '#3b82f6' : '#f59e0b',
                  color: 'white', padding: '2px 8px', borderRadius: '12px'
                }}>
                  {userType === 'system' ? `System ${currentUser?.role}` : `Company ${currentUser?.role}`}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {currentUser?.email || currentUser?.Usercomp_email}
              </div>
            </div>
          </div>
          {userType === 'company' && company && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>Company Code:</div>
              <div style={{ fontWeight: 'bold' }}>{company.company_code}</div>
            </div>
          )}
        </div>

        {/* Search box */}
        {isSystemAdmin && (
          <div className="search-container">
            <div className="search-wrapper">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search companies by name, code, type, or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input with-icon"
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm("")} title="Clear search">
                  ×
                </button>
              )}
            </div>
          </div>
        )}

        <div className="table-container">
          <table className="styled-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Code</th>
                <th>Company Name</th>
                <th>Business Type</th>
                <th>Country</th>
                <th>Currency</th>
                <th>Fiscal Year</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">
                    <div className="no-data-content">
                      <Building2 size={48} />
                      <p>
                        {searchTerm
                          ? `No companies found matching "${searchTerm}"`
                          : userType === 'company'
                            ? "No company data found for your account"
                            : "No companies found"
                        }
                      </p>
                      {searchTerm && isSystemAdmin && (
                        <button className="clear-search" onClick={() => setSearchTerm("")}>
                          Clear search
                        </button>
                      )}
                      {!searchTerm && isSystemAdmin && (
                        <button className="submit-btn" onClick={handleAddCompany} style={{ marginTop: '15px' }}>
                          <Plus size={16} className="mr-2" /> Add Your First Company
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((c, i) => (
                  <tr key={c._id}>
                    <td>{i + 1}</td>
                    <td><span className="company-code">{c.company_code}</span></td>
                    <td>
                      <div className="company-name-cell">
                        <strong>{c.company_name}</strong>
                        {c.created_by && <small>Created by: {c.created_by.Usercomp_sure_name}</small>}
                      </div>
                    </td>
                    <td><span className="type-badge">{c.business_type?.name || "-"}</span></td>
                    <td>{c.country?.name || "-"}</td>
                    <td><span className="currency-badge">{c.base_currency?.symbol || ""} {c.base_currency?.name || "-"}</span></td>
                    <td>
                      {c.fiscal_year_start
                        ? new Date(c.fiscal_year_start).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
                        : "Not set"}
                    </td>
                    <td><span className={`status-badge ${c.status}`}>{c.status}</span></td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn view-btn" onClick={() => handleView(c._id)} title="View Details">
                          <Eye size={18} />
                        </button>

                        {(
                          isSystemAdmin ||
                          (userType === "company" && currentUser?.role === "admin" && company?._id === c._id)
                        ) && (
                          <>
                            <button className="action-btn edit-btn" onClick={() => handleEdit(c._id)} title="Edit">
                              <Edit size={18} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDelete(c._id, c.company_name)}
                              disabled={deleting === c._id}
                              title="Delete"
                            >
                              {deleting === c._id ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}
                            </button>
                          </>
                        )}

                        {(userType === 'company' && currentUser?.role === 'admin' && company?._id === c._id) ||
                          (isSystemAdmin && (
                            <button className="action-btn add-btn" onClick={() => handleAddUser(c._id)} title="Add User">
                              <Users size={18} />
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {isSystemAdmin && filteredCompanies.length > 0 && searchTerm && (
          <div className="search-info">
            Showing {filteredCompanies.length} of {companies.length} companies
            {searchTerm && ` matching "${searchTerm}"`}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyList;